/**
 * Décodage de la charge utile d'un JWT — **sans vérification de signature**.
 *
 * ⚠️ À n'utiliser que pour des décisions **locales et non sensibles** : savoir de quel utilisateur
 * et de quel tenant parle le jeton qu'on tient déjà. La vérification, elle, est faite par le
 * serveur à chaque requête ; rien ici n'autorise quoi que ce soit.
 *
 * Générique pour laisser l'appelant nommer la forme qu'il attend
 * (`parseJwtPayload<IDecodedToken>(t)`) : sans ça, un `Record<string, any>` ne correspond
 * structurellement à aucune interface nommée et l'appelant se prend un TS2345.
 */
export function parseJwtPayload<T = Record<string, any>>(token: string): T | null {
  try {
    const base64Url = token.split(".")[1]
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/")
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(c => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    )

    return JSON.parse(jsonPayload)
  } catch (error) {
    console.error("Invalid JWT token", error)
    return null
  }
}

/**
 * Identité portée par un jeton : `"<businessId>:<userId>"`, ou `null` s'il n'y a pas de jeton.
 *
 * Sert à distinguer « **qui** est connecté a changé » de « la chaîne du jeton a changé ». Les deux
 * ne sont pas synonymes : `GET /v2/user` **réémet** un jeton pour le *même* utilisateur (nouvel
 * `iat`, donc nouvelle chaîne). Traiter cette réémission comme une bascule de compte déclencherait
 * une purge de cache et un rejeu de toutes les requêtes actives en pleine session, pour rien.
 *
 * ⚠️ **Repli sur la chaîne brute dans DEUX cas**, et le second n'est pas décoratif :
 *
 * 1. le jeton est illisible (pas un JWT, base64 cassé) ;
 * 2. la charge utile se décode mais ne porte **ni `businessId` ni `userId`**.
 *
 * Sans le cas 2, un jeton d'une autre forme rendrait `":"` — la **même** identité pour tout le
 * monde — donc plus aucune bascule de compte ne serait détectée et la purge deviendrait
 * silencieusement inerte : très exactement le défaut que ce fichier existe pour fermer. Ces jetons
 * existent (le back legacy émet un jeton d'inscription qui ne porte que `email`), et un renommage
 * de claim côté serveur produirait le même effet sans qu'aucun test ne rougisse. La règle est donc
 * celle du fail-closed : on préfère purger de trop (une purge inutile coûte un rejeu) que de trop
 * peu (servir les données du tenant précédent).
 */
export function authIdentityFromToken(token: string | null | undefined): string | null {
  if (!token) return null
  const payload = parseJwtPayload<Record<string, any>>(token)
  if (!payload) return token
  if (payload.businessId == null && payload.userId == null) return token
  return `${payload.businessId ?? ""}:${payload.userId ?? ""}`
}
